import {
  faArrowDownShortWide,
  faArrowUpArrowDown,
  faArrowUpWideShort,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { useCallback, useEffect, useState } from 'react';

import Box from '~/common/components/Box';
import Button from '~/common/components/Button';
import Checkbox from '~/common/components/Checkboxes/Checkbox/Checkbox';
import ConfirmationDialog from '~/common/components/ConfirmationDialog';
import Flex from '~/common/components/Flex';
import Spinner from '~/common/components/Spinner';
import { Tbody, Td, Thead, Tr } from '~/common/components/Tables';
import Text from '~/common/components/Texts/Text';
import useUrl from '~/common/hooks/useUrl';
import theme from '~/theme/';

import CrudGridToolbar from './CrudGridToolbar';
import filterGridData from './Filters/filterGridData';
import type { GridDef } from './GridDef';
import {
  StyledDeleteMultipleBtnGroup,
  StyledFontAwesomeIcon,
  StyledTable,
  StyledTableHead,
  StyledTr,
} from './GridStyledComponents';
import { SortIcon } from './SortIcon';
import { type ColumnSort, handleSettingColumnSort } from './crudGridUtils';

export type CrudGridProps = {
  // This your data as an array of objects
  data: any[];
  // The object that represents the row that is currently selected
  selectedRow?: any;
  // The grid def contains all the information needed to render the grid
  gridDef: GridDef;
  readOnly?: boolean;
  columns: {
    // fieldName is the attribute name that represents the data you want in that grid
    fieldName?: string;
    // The display text that is shown in the Header for the column
    headerName?: string;
    // Boolean that controls if you can sort on this filter, if true you get the icons and the action otherwise false you don't see icons
    sortable?: boolean;
    // If isNumeric value is aligned to the right as oppose to the left
    isNumeric?: boolean;
    // Function that would allow the raw data to be translated to more readable values, like boolean to Yes or No, or format currency
    renderCell?: (...args: any[]) => any;
    // Boolean that if true would pass a second argument of dropdownOptions back to the callback function renderCell. The dropdownOptions is an object you pass to the grid tied to state incase the options come from an API call
    useOptions?: boolean;
  }[];
  filters?: any[];
  // function to handle when a row is clicked
  onRowEditClick?: (...args: any[]) => any;
  // boolean to control if editRow is disabled
  disableRowClick?: boolean;
  // boolean to control if deleteRow is disabled
  disableRowDelete?: boolean;
  // function to handle when delete is clicked
  onDelete?: (...args: any[]) => any;
  isLoading?: boolean;
  // Object with attributes referencing all arrays of options in case a value needs to be mapped.
  dropdownOptions?: any;
  multiSelectActionDef?: {
    buttonLabel?: string;
    subActions?: any;
  };
  onGridModeChange?: (...args: any[]) => any;
  summary?: React.ReactNode;
  actions?: React.ReactNode;
  // Enabling Audit History is depending on isUndwriter && displayAuditHistory returned as true
  isAuditHistoryEnabled?: boolean;
};

/**
 * A reusable CRUD grid component for displaying and managing data.
 * @component
 * @param {Object} props - The component props.
 * @param {Array} props.data - An array of objects representing the data to be displayed in the grid.
 * @param {Object} props.selectedRow - The object representing the currently selected row.
 * @param {Object} props.gridDef - The grid definition object containing configuration options for the grid.
 * @param {Array} props.columns - An array of column objects defining the columns to be displayed in the grid.
 * @param {Function} props.onRowEditClick - The function to be called when a row is clicked for editing.
 * @param {Function} props.onDelete - The function to be called when the delete button is clicked.
 * @param {boolean} props.isLoading - A boolean indicating whether the grid is in a loading state.
 * @returns {JSX.Element} The rendered CRUD grid component.
 */

export default function CrudGrid({
  gridDef,
  columns,
  data,
  onRowEditClick,
  selectedRow,
  onDelete,
  isLoading = true,
  dropdownOptions,
  readOnly,
  filters,
  multiSelectActionDef,
  onGridModeChange,
  summary,
  actions,
  disableRowClick = false,
  isAuditHistoryEnabled,
  disableRowDelete = false,
}: Readonly<CrudGridProps>) {
  const {
    defaultSort,
    uniqueIdAttributeFields,
    getDialogDeleteMessage,
    gridLanguage,
    allowFilters,
  } = gridDef;
  const { isReadOnly } = useUrl();
  const [gridDisplayData, setGridDisplayData] = useState(data);
  const [showDeleteMultiple, setShowDeleteMultiple] = useState(false);
  const [gridSort, setGridSort] = useState<ColumnSort>(() => {
    return defaultSort
      ? {
          field: defaultSort.field,
          direction: defaultSort.direction ?? '',
          icon:
            defaultSort.direction === 'desc'
              ? faArrowUpWideShort
              : faArrowDownShortWide,
        }
      : { field: '', direction: '' };
  });
  const [appliedFilters, setAppliedFilters] = useState([]);

  const [checkedItems, setCheckedItems] = useState([]);
  const [selectDeselectAll, setSelectDeselectAll] = useState(false);

  useEffect(() => {
    setGridDisplayData(filterGridData([...data], appliedFilters, gridSort));
  }, [data, appliedFilters, gridSort]);

  useEffect(() => {
    setShowDeleteMultiple(false);
  }, [data]);

  const getUniqueId = (row) => {
    let id = '';
    uniqueIdAttributeFields.forEach((field) => {
      const fieldPath = field.split('.');
      const value = fieldPath.reduce(
        (obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : ''),
        row,
      );
      id += value;
    });
    return id;
  };

  const getBackgroundColor = (row, i: number) => {
    if (getUniqueId(row) === getUniqueId(selectedRow)) {
      return theme?.colors?.blue[50];
    }

    return i % 2 === 0 ? theme?.colors?.bgGray[100] : '';
  };

  const handleRowClick = useCallback((row, e) => {
    if (!e.target.closest('.crud-grid-action-cell') && !showDeleteMultiple) {
      onRowEditClick(row);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  /**
   * Sets the gridSort state variable that controls how the grid is currently sorted.
   * @function handleColumnSortClick
   * @param {Object} column - The column object.
   * @param {string} column.fieldName - The field name of the column.
   * @param {string} column.headerName - The header display name of the column.
   */
  const handleColumnSortClick = (column) => {
    setGridSort(
      handleSettingColumnSort(
        column,
        gridSort,
        faArrowUpArrowDown,
        faArrowDownShortWide,
        faArrowUpWideShort,
      ),
    );
  };

  const isSelectedRow = (id) => {
    const foundId = checkedItems.find((item) => getUniqueId(item) === id);
    return foundId !== undefined;
  };

  useEffect(() => {
    // When the Delete Multiple session is cancelled, all checkboxes should be unchecked
    if (!showDeleteMultiple) {
      setSelectDeselectAll(!selectDeselectAll);
      setCheckedItems([]);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [showDeleteMultiple]);

  return (
    <Box
      className="crudGrid"
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'column',
      }}
    >
      {(allowFilters || multiSelectActionDef || isAuditHistoryEnabled) &&
        !isReadOnly && (
          <CrudGridToolbar
            allowFilters={allowFilters ?? false}
            isReadOnly={readOnly}
            multiSelectAction={multiSelectActionDef}
            isMultiSelectActionMode={showDeleteMultiple}
            filters={filters}
            appliedFilters={appliedFilters}
            onFilterChange={(filters) => setAppliedFilters(filters)}
            onActionClick={() => {
              onGridModeChange();
              setShowDeleteMultiple(!showDeleteMultiple);
            }}
            onSubActionClick={(action) => {
              setCheckedItems((prev) => action.action(gridDisplayData, prev));
            }}
            isAuditHistoryEnabled={isAuditHistoryEnabled}
          />
        )}

      <StyledTable size="sm" colorScheme="gray" w="100%">
        <Thead
          style={{
            backgroundColor: isReadOnly
              ? theme.colors?.bgGray[300]
              : theme.colors?.blue[600],
            zIndex: 1,
          }}
        >
          <Tr>
            {columns.map((column) => (
              <StyledTableHead
                key={column.headerName}
                scope="col"
                onClick={() => handleColumnSortClick(column)}
                isReadOnlyUrl={isReadOnly}
              >
                <Flex
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Text>{column.headerName}</Text>
                  <SortIcon
                    fieldName={column.fieldName}
                    sortable={column.sortable}
                    gridSort={gridSort}
                    isReadOnlyUrl={isReadOnly}
                  />
                </Flex>
              </StyledTableHead>
            ))}
            {!readOnly && (
              <StyledTableHead
                textAlign={'start'}
                scope="col"
                isReadOnlyUrl={isReadOnly}
              >
                Delete
              </StyledTableHead>
            )}
          </Tr>
        </Thead>
        <Tbody>
          {gridDisplayData.length === 0 && !isLoading && (
            <Tr>
              <Td
                colSpan={columns.length + 1}
                sx={{
                  textAlign: 'center',
                }}
              >
                No Data to Display
              </Td>
            </Tr>
          )}
          {isLoading && gridDisplayData.length === 0 && (
            <Tr>
              <Td
                colSpan={columns.length + 1}
                sx={{
                  textAlign: 'center',
                }}
              >
                <Spinner backgroundColor={'none'} height={'20rem'} />
              </Td>
            </Tr>
          )}
          {gridDisplayData.map((row, i) => (
            <StyledTr
              key={getUniqueId(row)}
              disableRowClick={disableRowClick}
              background={getBackgroundColor(row, i)}
              data-testid={`crud-grid-row-${i}`}
              aria-selected={
                getUniqueId(row) === getUniqueId(selectedRow) ? 'true' : 'false'
              }
              onClick={
                disableRowClick ? undefined : (e) => handleRowClick(row, e)
              }
            >
              {columns.map((column) => {
                let cellValue;
                if (column.renderCell) {
                  const options = column.useOptions
                    ? (dropdownOptions ?? '')
                    : '';
                  cellValue = column.renderCell(row[column.fieldName], options);
                } else {
                  cellValue = row[column.fieldName];
                }
                return (
                  <Td
                    key={column.headerName}
                    sx={{
                      borderRight: `1px solid ${theme?.colors?.gray[200]}`,
                      textAlign: column.isNumeric ? 'right' : 'left',
                      lineHeight: '1.75',
                    }}
                  >
                    {cellValue}
                  </Td>
                );
              })}
              {!readOnly && (
                <Td
                  sx={{
                    borderRight: `1px solid ${theme?.colors?.gray[200]}`,
                    textAlign: 'center',
                  }}
                  className="crud-grid-action-cell"
                >
                  {showDeleteMultiple ? (
                    <Checkbox
                      id={`checkboxDelete-${i}`}
                      name={`checkboxDelete-${i}`}
                      data-testid={`checkboxDelete-${i}`}
                      isChecked={isSelectedRow(getUniqueId(row))}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCheckedItems((prev) => [...prev, row]);
                        } else {
                          setCheckedItems(
                            checkedItems.filter(
                              (item) => getUniqueId(item) !== getUniqueId(row),
                            ),
                          );
                        }
                      }}
                    />
                  ) : (
                    <ConfirmationDialog
                      message={getDialogDeleteMessage([row])}
                      itemName={gridLanguage.singularItem}
                      isDeleteDisabled={
                        (row.isSubjectProperty &&
                          gridLanguage.singularItem === 'REO') ||
                        disableRowDelete
                      }
                      buttonId={`delete-button-${i}`}
                      onClick={() => onDelete([row])}
                    >
                      <StyledFontAwesomeIcon
                        icon={faTrash}
                        data-action="delete"
                        data-testid={`delete-grid-icon-button-${i}`}
                      />
                    </ConfirmationDialog>
                  )}
                </Td>
              )}
            </StyledTr>
          ))}
        </Tbody>
      </StyledTable>
      {!isLoading && (
        <Box>
          {summary && (
            <Flex justifyContent={'flex-end'} mt="1.5rem">
              {summary}
            </Flex>
          )}
          {actions && !readOnly && !showDeleteMultiple && (
            <Flex justifyContent="flex-end" gap="0.75rem" mt="1.5rem">
              {actions}
            </Flex>
          )}
          {showDeleteMultiple && (
            <StyledDeleteMultipleBtnGroup flexDirection="row">
              <Button
                isPrimary={false}
                onClick={() => setShowDeleteMultiple(false)}
              >
                Cancel
              </Button>
              <ConfirmationDialog
                message={getDialogDeleteMessage(checkedItems)}
                itemName={
                  checkedItems.length > 1
                    ? gridLanguage.pluralItem
                    : gridLanguage.singularItem
                }
                status=""
                onClick={() => onDelete(checkedItems)}
              >
                <Button
                  isPrimary={true}
                  isDisabled={checkedItems.length === 0}
                  transition="width 0.5s ease-in-out"
                  data-testid="confirm-delete-multiple-button"
                  ml="1rem"
                >
                  Delete {gridLanguage.pluralItem}
                </Button>
              </ConfirmationDialog>
            </StyledDeleteMultipleBtnGroup>
          )}
        </Box>
      )}
    </Box>
  );
}
